import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Shield, TrendingUp, Loader2 } from 'lucide-react';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import api from '../lib/api';

const GRADE_STYLES = {
  green: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900 dark:text-green-200 dark:border-green-700',
  blue: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-700',
  amber: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900 dark:text-amber-200 dark:border-amber-700',
  gray: 'bg-muted text-muted-foreground border-border',
};

/**
 * Compact trust score badge for directory cards and profile headers.
 * Shows score number + grade color. Tooltip shows breakdown.
 */
export function TrustScoreBadge({ userId, orgId, size = 'sm' }) {
  const { data: scoreData, isLoading } = useQuery({
    queryKey: ['trust-score', userId || orgId],
    queryFn: () => {
      if (orgId) return api.get(`/risk/score/org/${orgId}`).then(r => r.data);
      if (userId) return api.get(`/risk/score/${userId}`).then(r => r.data);
      return null;
    },
    enabled: !!(userId || orgId),
    staleTime: 5 * 60 * 1000, // Cache 5 min
    retry: false,
  });

  if (isLoading) return <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />;
  if (!scoreData || scoreData.score === 0) return null;

  const style = GRADE_STYLES[scoreData.grade_color] || GRADE_STYLES.gray;

  if (size === 'xs') {
    return (
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium border ${style}`}>
        <Shield className="h-3 w-3" />
        {scoreData.score}
      </span>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-semibold border cursor-help ${style}`}>
            <Shield className="h-3.5 w-3.5" />
            {scoreData.score}/100
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-1">
            <p className="font-semibold">Trust Score: {scoreData.score}/100 ({scoreData.grade})</p>
            {Object.entries(scoreData.breakdown || {}).map(([key, val]) => (
              <div key={key} className="flex justify-between text-xs">
                <span className="text-muted-foreground">{val.detail || key}</span>
                <span className="font-mono">{val.score}/{val.max}</span>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Full trust score card for profile pages and dashboard.
 */
export function TrustScoreCard({ userId, showSuggestions = false }) {
  const endpoint = showSuggestions ? '/risk/my-score?include_narrative=true' : `/risk/score/${userId}?include_narrative=true`;

  const { data: scoreData, isLoading } = useQuery({
    queryKey: ['trust-score-full', userId || 'me'],
    queryFn: () => api.get(endpoint).then(r => r.data),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!scoreData) return null;

  const circumference = 2 * Math.PI * 45;
  const offset = circumference - (scoreData.score / 100) * circumference;
  const gradeColor = scoreData.grade_color === 'green' ? '#22c55e' :
    scoreData.grade_color === 'blue' ? '#3b82f6' :
    scoreData.grade_color === 'amber' ? '#f59e0b' : '#9ca3af';

  return (
    <div className="space-y-4">
      {/* Score Circle */}
      <div className="flex items-center gap-6">
        <div className="relative w-28 h-28">
          <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="8" className="text-muted/20" />
            <circle cx="50" cy="50" r="45" fill="none" stroke={gradeColor} strokeWidth="8"
              strokeDasharray={circumference} strokeDashoffset={offset}
              strokeLinecap="round" className="transition-all duration-1000" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-bold">{scoreData.score}</span>
            <span className="text-xs text-muted-foreground">/ 100</span>
          </div>
        </div>
        <div>
          <p className="text-lg font-semibold">{scoreData.grade}</p>
          {scoreData.narrative && (
            <p className="text-sm text-muted-foreground mt-1">{scoreData.narrative}</p>
          )}
        </div>
      </div>

      {/* Breakdown Bars */}
      <div className="space-y-2">
        {Object.entries(scoreData.breakdown || {}).map(([key, val]) => (
          <div key={key} className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">{val.detail || key}</span>
              <span className="font-mono font-medium">{val.score}/{val.max}</span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${(val.score / val.max) * 100}%`,
                  backgroundColor: gradeColor
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Suggestions */}
      {showSuggestions && scoreData.suggestions?.length > 0 && (
        <div className="pt-2 border-t border-border">
          <p className="text-sm font-medium mb-2 flex items-center gap-1">
            <TrendingUp className="h-4 w-4" />
            Improve Your Score (potential: {scoreData.potential_score})
          </p>
          <div className="space-y-2">
            {scoreData.suggestions.map((s, i) => (
              <div key={i} className="text-xs p-2 rounded bg-muted/50">
                <p className="font-medium">{s.message}</p>
                <p className="text-muted-foreground">{s.action} (+{s.potential_gain} pts)</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default TrustScoreBadge;
